import { useMemo } from 'react'

import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { useGetDataForExport } from '@/hooks/exportDropdown'
import type { ExportBlockItem, ExportData } from '@/hooks/exportDropdown/types'
import { getBlockContentText } from '@/utils'
import { stringifyCsvRow } from '@/utils/csv'
import type { ParseProjectItem, UseControlsReducerState } from '../types'

const escapeSeparators = (text?: string) => {
  if (!text) {
    return
  }

  return `${text}`
}

const parseProject = (
  state: UseControlsReducerState,
  item: ParseProjectItem,
  separator: string = ';'
) => {
  const result = [
    escapeSeparators(item.parentId),
    escapeSeparators(item.pageId),
    escapeSeparators(item.pageName),
  ]

  if (!state.withoutBlocks) {
    result.push(escapeSeparators(item.blockTitle))
  }

  if (state.isNotes) {
    result.splice(4, 0, escapeSeparators(item.blockContent))
  }

  if (state.isLinks) {
    result.splice(state.isNotes ? 5 : 4, 0, escapeSeparators(item.pageUrl))
  }

  if (state.isSeoMetaTags) {
    result.push(
      escapeSeparators(item.seoTitle),
      escapeSeparators(item.seoSlug),
      escapeSeparators(item.seoDescription),
      escapeSeparators(item.seoH1),
      escapeSeparators(item.seoNote),
      escapeSeparators(item.keywords),
      escapeSeparators(item.pageIntent)
    )
  }

  return stringifyCsvRow(result, separator)
}

const getProjectHeader = (
  state: UseControlsReducerState,
  separator: string = ';'
): string => {
  const result = state.withoutBlocks
    ? ['Parent Page ID', 'Page ID', 'Page title']
    : ['Parent Page ID', 'Page ID', 'Page title', 'Block title']

  if (state.isNotes) {
    result.splice(4, 0, 'Block content')
  }

  if (state.isLinks) {
    result.splice(state.isNotes ? 5 : 4, 0, 'Page link')
  }

  if (state.isSeoMetaTags) {
    result.push(
      'SEO title',
      'SEO slug',
      'SEO description',
      'SEO H1',
      'SEO additional',
      'SEO keywords',
      'SEO page intent'
    )
  }

  return result.join(separator)
}

const generate = (
  tree: ExportData[],
  separator: string = ';',
  withoutBlocks?: boolean
) => {
  const result: ParseProjectItem[] = []

  tree.forEach((item) => {
    result.push({
      parentId: item.parentId || 'null',
      pageId: item.id,
      pageName: item.title || DEFAULT_PAGE_NAME,
      blockTitle: '',
      blockContent: '',
      pageUrl: item.url || 'null',
      seoTitle: item.notes?.seoTitle || 'null',
      seoSlug: item.notes?.seoSlug || 'null',
      seoDescription: item.notes?.seoDescription || 'null',
      seoH1: item.notes?.seoH1 || 'null',
      seoNote: getBlockContentText(item.notes?.note) || 'null',
      keywords: item.notes?.keywords || 'null',
      pageIntent: item.notes?.pageIntent || 'null',
    })

    if (item.blocks.length && !withoutBlocks) {
      item.blocks.forEach((block: ExportBlockItem) => {
        result.push({
          parentId: block.parentId || 'null',
          pageId: block.nodeId,
          pageName: '',
          blockTitle: block.title || 'Block',
          blockContent: getBlockContentText(block.content) || 'null',
          pageUrl: '',
          seoTitle: '',
          seoSlug: '',
          seoDescription: '',
          seoH1: '',
          seoNote: '',
          keywords: '',
          pageIntent: '',
        })
      })
    }

    if (item.children.length > 0) {
      result.push(...generate(item.children, separator, withoutBlocks))
    }
  })

  return result
}

export const useSubmitExportAsCsv = (state: UseControlsReducerState) => {
  const { getExportData } = useGetDataForExport()
  const exportData = getExportData()

  const separator = state.isCommaSeparated ? ',' : ';'

  const csvData = generate(
    exportData.flat(),
    separator,
    state.withoutBlocks
  ).map((item) => parseProject(state, item, separator))

  const result = useMemo(
    () => ({
      project: [
        ...(state.isHeadersSet ? [getProjectHeader(state, separator)] : []),
        ...csvData,
      ],
    }),
    [state, csvData, separator]
  )

  const handleSubmit = () => {
    return new Promise((resolve) => {
      resolve(
        new Blob([result.project.map((item) => item.trim()).join('\n')], {
          type: 'application/csv;charset=utf-8',
        })
      )
    })
  }

  return {
    handleSubmit,
    result,
  }
}
